import type { ReactNode } from 'react';
import {
  Cell,
  Column,
  EmptyState,
  Row,
  Table,
  TableBody,
  TableContainer,
  TableHeader,
} from '@skupreme/shared/ui';

export type DataColumn<T> = {
  id: string;
  header: string;
  cell: (row: T) => ReactNode;
};

type DataTableProps<T> = {
  /** Names the table for screen readers. */
  label: string;
  columns: DataColumn<T>[];
  rows: T[];
  getKey: (row: T) => string;
  /** Shown instead of the table when there are no rows. */
  empty: string;
};

export function DataTable<T extends object>({
  label,
  columns,
  rows,
  getKey,
  empty,
}: DataTableProps<T>) {
  if (rows.length === 0) {
    return <EmptyState title={empty} />;
  }
  return (
    <TableContainer>
      <Table aria-label={label}>
        <TableHeader>
          {columns.map((column, index) => (
            <Column key={column.id} id={column.id} isRowHeader={index === 0}>
              {column.header}
            </Column>
          ))}
        </TableHeader>
        <TableBody>
          {rows.map(row => (
            <Row key={getKey(row)} id={getKey(row)}>
              {columns.map(column => (
                <Cell key={column.id}>{column.cell(row)}</Cell>
              ))}
            </Row>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
